import { Action, ActionReducer, createFeatureSelector, INIT, MemoizedSelector } from '@ngrx/store';

/**
 * Merges several reducers so that they share a common state
 * @param reducers
 */
export function createMergedReducer(
  ...reducers: ActionReducer<any, Action>[]
): ActionReducer<any, Action> {
  const mergedReducer = mergeReducers(...reducers);

  return (state: any, action: Action) => {
    if (reducers.length === 0) {
      throw new Error('At least one reducer was expected');
    }

    if (action.type === INIT) {
      // INIT action is a special case
      // we need to merge the initial state from each reducer
      // which is returned as the merged reducer's complete initial state
      return reducers.reduce(
        (mergedInitialState, reducer) => ({ ...mergedInitialState, ...reducer(state, action) }),
        state,
      );
    } else {
      // for all other actions, we dispatch the action to each reducer sequentially
      // which performs discrete updates on the merged state
      return mergedReducer(state, action);
    }
  };
}

export function mergeReducers(...reducers: ((S, A) => typeof S)[]) {
  return (state, action) =>
    reducers.reduce((mergedState, reduce) => reduce(mergedState, action), state);
}

/**
 * returns a selector that selects the top level state of an adapter
 *
 * @param stateKeyOrSelector either a string which represents the feature key for the adapter's state,
 * or a feature selector that references state for the adapter
 */
export function getAdapterSelector<TResult>(
  stateKeyOrSelector: string | MemoizedSelector<object, TResult>,
) {
  function isSelector<T>(
    selector: string | MemoizedSelector<object, T>,
  ): selector is MemoizedSelector<object, T> {
    return typeof selector === 'function';
  }

  return isSelector(stateKeyOrSelector)
    ? stateKeyOrSelector
    : createFeatureSelector<TResult>(stateKeyOrSelector);
}
